import React from 'react';
import PropTypes from 'prop-types';
import { EM_DASH, formatEtDateTime } from './queueMonitorFormat';

/**
 * HealthBanner
 * Reasons-driven replacement for the in-file HealthStatus memo component.
 *
 * - `health.reasons` present (new backend) -> severity-colored rows, each rendering the
 *   machine-readable reason {code, severity, message, metrics} verbatim. Rows navigate
 *   to the relevant tab via onNavigate (locked code->tab map below).
 * - `health.reasons` absent (old backend, deploy skew) -> legacy fallback: today's exact
 *   rendering of status + issues[].
 * - `health.warming_up` -> explicit muted note row; sustained-window checks are paused.
 */

const SEVERITY_COLORS = {
	critical: '#dc3545',
	warning: '#ffc107',
	info: '#0d6efd',
	healthy: '#28a745',
};

const SEVERITY_ICONS = {
	critical: 'bi bi-x-octagon-fill',
	warning: 'bi bi-exclamation-triangle-fill',
	info: 'bi bi-info-circle-fill',
};

// Locked reason-code -> tab map (v1 contract 6.6; v2 adds regression_vs_baseline;
// v3 adds workers_dead/workers_degraded -> processing; the email outbox rules
// open the Email outbox tab; retry_loop and dead_dependency open the failed tab).
// null = not clickable.
const REASON_TAB_MAP = {
	stuck_jobs: 'stuck',
	failure_spike: 'failed',
	retry_loop: 'failed',
	dead_dependency: 'failed',
	workers_dead: 'processing',
	workers_degraded: 'processing',
	backlog_compounding: 'pending',
	stale_backlog: 'pending',
	user_facing_stale: 'overview',
	sampler_stale: null,
	fanout_burst: 'pending',
	regression_vs_baseline: 'failed',
	alert_outbox_stalled: 'outbox',
	outbox_rows_stale: 'outbox',
};

// v3 contract 3.5 / 5.3: the last-alert chip on the banner. Reads the most
// recent queue_monitor_alerts row folded into the health/stats payload.
const LastAlertChip = ({ lastAlert }) => {
	// `last_alert` block absent (pre-v3 backend) -> render nothing (deploy skew).
	if (lastAlert === undefined) {
		return null;
	}
	// Present but null -> the system has never paged: an honest "No alerts" chip.
	if (lastAlert === null) {
		return (
			<span className="last-alert-chip badge bg-light text-muted ms-2">
				No alerts
			</span>
		);
	}
	const severity = (lastAlert.severity || 'info').toLowerCase();
	const color = SEVERITY_COLORS[severity] || '#6c757d';
	const kind = lastAlert.kind === 'recovery' ? 'recovered' : 'paged';
	const codes = lastAlert.reason_codes || '';
	const at = lastAlert.at ? `${formatEtDateTime(lastAlert.at)} ET` : '';
	const sentNote = lastAlert.sent === false ? ' (send failed)' : '';
	return (
		<span
			className="last-alert-chip badge ms-2"
			style={{ backgroundColor: color, color: '#fff' }}
			title={`Last ${kind}: ${severity.toUpperCase()} ${codes} ${at}${sentNote}`}
		>
			Last {kind}: {severity.toUpperCase()}
			{codes ? ` ${codes}` : ''} {at}
			{sentNote}
		</span>
	);
};

LastAlertChip.propTypes = {
	lastAlert: PropTypes.oneOfType([PropTypes.object]),
};

LastAlertChip.defaultProps = {
	lastAlert: undefined,
};

const statusColor = (status) => SEVERITY_COLORS[status] || '#6c757d';

// A by_queue entry's headline number: its first numeric field after the queue name
// (pending for a backlog, failure_rate_1h for a regression, alive for workers).
const byQueueEntryValue = (entry) => {
	const found = Object.entries(entry).find(
		([key, value]) => key !== 'queue' && typeof value === 'number' && Number.isFinite(value),
	);
	return found ? found[1] : null;
};

const formatMetricValue = (value) => {
	if (value === null || value === undefined) {
		return EM_DASH;
	}
	// A by_queue list renders one `queue: value` pair per entry.
	if (Array.isArray(value)) {
		return value
			.map((entry) =>
				entry && typeof entry === 'object' && entry.queue !== undefined
					? `${entry.queue}: ${formatMetricValue(byQueueEntryValue(entry))}`
					: formatMetricValue(entry),
			)
			.join(', ');
	}
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value.toLocaleString('en-US');
	}
	return String(value);
};

const metricsText = (metrics) =>
	Object.entries(metrics || {})
		.map(([key, value]) => `${key}: ${formatMetricValue(value)}`)
		.join(' | ');

const ReasonRow = ({ reason, onNavigate }) => {
	const color = SEVERITY_COLORS[reason.severity] || '#6c757d';
	const icon = SEVERITY_ICONS[reason.severity] || 'bi bi-question-circle';
	const targetTab = REASON_TAB_MAP[reason.code] || null;
	const clickable = Boolean(onNavigate) && targetTab !== null;
	const metrics = metricsText(reason.metrics);

	const content = (
		<React.Fragment>
			<i className={`${icon} me-2`} style={{ color }} aria-hidden="true" />
			<span className="health-reason-message">{reason.message}</span>
			{metrics && (
				<span className="health-reason-metrics text-muted small ms-2">{metrics}</span>
			)}
		</React.Fragment>
	);

	if (clickable) {
		return (
			<button
				type="button"
				className="health-reason-row health-reason-clickable d-block w-100 text-start border-0 bg-transparent px-2 py-1"
				style={{ borderLeft: `4px solid ${color}`, cursor: 'pointer' }}
				onClick={() => onNavigate(targetTab)}
			>
				{content}
			</button>
		);
	}

	return (
		<div
			className="health-reason-row px-2 py-1"
			style={{ borderLeft: `4px solid ${color}` }}
		>
			{content}
		</div>
	);
};

ReasonRow.propTypes = {
	reason: PropTypes.shape({
		code: PropTypes.string,
		severity: PropTypes.string,
		message: PropTypes.string,
		metrics: PropTypes.object,
	}).isRequired,
	onNavigate: PropTypes.func,
};

ReasonRow.defaultProps = {
	onNavigate: undefined,
};

const HealthBanner = ({ health, onNavigate }) => {
	if (!health) {
		return null;
	}

	const color = statusColor(health.status);
	// `last_alert` is undefined on a pre-v3 backend (chip hidden), null when the
	// system has never paged ("No alerts"), or an object for the most recent page.
	const hasLastAlertKey = Object.prototype.hasOwnProperty.call(
		health,
		'last_alert',
	);
	const header = (
		<div className="health-status-header">
			<h3>
				Health Status:{' '}
				<span style={{ color }}>
					{(health.status || 'unknown').toUpperCase()}
				</span>
				{hasLastAlertKey && <LastAlertChip lastAlert={health.last_alert} />}
			</h3>
			<span className="health-timestamp">{health.timestamp}</span>
		</div>
	);

	// Old backend (deploy skew): no reasons array -> legacy status + issues[] rendering.
	if (!Array.isArray(health.reasons)) {
		return (
			<div
				className="health-status mb-4"
				style={{ borderLeftColor: color }}
			>
				{header}
				{health.issues && health.issues.length > 0 && (
					<ul className="health-issues">
						{health.issues.map((issue, i) => (
							<li key={i}>{issue}</li>
						))}
					</ul>
				)}
			</div>
		);
	}

	const warmingRow = health.warming_up === true && (
		<div className="health-warming-row text-muted small px-2 py-1">
			<i className="bi bi-hourglass-split me-2" aria-hidden="true" />
			throughput history warming up - sustained-window checks paused
			{health.history_reset_at
				? ` (history reset ${formatEtDateTime(health.history_reset_at)} ET)`
				: ''}
		</div>
	);

	return (
		<div className="health-status mb-4" style={{ borderLeftColor: color }}>
			{header}
			{warmingRow}
			{health.reasons.length > 0 && (
				<div className="health-reasons">
					{health.reasons.map((reason, i) => (
						<ReasonRow
							key={`${reason.code || 'reason'}-${i}`}
							reason={reason}
							onNavigate={onNavigate}
						/>
					))}
				</div>
			)}
			{health.reasons.length === 0 && health.status === 'healthy' && (
				<div
					className="health-reason-row health-all-passing px-2 py-1"
					style={{ borderLeft: `4px solid ${SEVERITY_COLORS.healthy}`, color: SEVERITY_COLORS.healthy }}
				>
					<i className="bi bi-check-circle-fill me-2" aria-hidden="true" />
					All queue health checks passing
				</div>
			)}
			{health.reasons.length === 0 && health.status !== 'healthy' &&
				health.issues && health.issues.length > 0 && (
				// Defensive: inconsistent payload (non-healthy status but no reasons) -
				// fall back to the legacy issue strings rather than claiming all checks pass.
				<ul className="health-issues">
					{health.issues.map((issue, i) => (
						<li key={i}>{issue}</li>
					))}
				</ul>
			)}
		</div>
	);
};

HealthBanner.propTypes = {
	health: PropTypes.object,
	onNavigate: PropTypes.func,
};

HealthBanner.defaultProps = {
	health: null,
	onNavigate: undefined,
};

export default HealthBanner;
